import React, { useEffect, useRef, useState } from "react";

import { Nullable } from "../../../common/types/types";

export interface IImageProps
	extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, "onError" | "onLoad" | "src"> {
	src: Nullable<string>;
	alt?: string;
	className?: string;
	placeholderSrc?: string;
	onLoad?: () => void;
	onError?: (error: Event | string) => void;
	retryDelay?: number;
	maxRetries?: number;
	disableRetry?: boolean;
	"data-testid"?: string;
}

const ImageComponent: React.FC<IImageProps> = ({
	src = "",
	alt = "",
	className = "",
	placeholderSrc,
	onLoad,
	onError,
	retryDelay = 1000,
	maxRetries = 3,
	disableRetry = false,
	...rest
}) => {
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<Event | string | null>(null);
	const [retries, setRetries] = useState(0);
	const imgRef = useRef<HTMLImageElement | null>(null);
	const placeholderLoaded = useRef(false);

	useEffect(() => {
		setLoading(true);
		setError(null);
		setRetries(0);

		const loadImage = () => {
			if (!imgRef.current || src === null) return;

			imgRef.current.onload = () => {
				setLoading(false);
				if (onLoad) {
					onLoad();
				}
			};

			imgRef.current.onerror = (event) => {
				if (!disableRetry && retries < maxRetries) {
					setTimeout(() => {
						setRetries((prev) => prev + 1);
						loadImage();
					}, retryDelay);
				} else {
					setLoading(false);
					setError(event);
					if (onError) {
						onError(event);
					}
				}
			};

			imgRef.current.src = src;
		};

		if (placeholderSrc && !placeholderLoaded.current) {
			const placeholder = new Image();

			placeholder.src = placeholderSrc;
			placeholderLoaded.current = true;
		}

		loadImage();

		return () => {
			if (imgRef.current) {
				imgRef.current.onload = null;
				imgRef.current.onerror = null;
			}
		};
	}, [src, placeholderSrc, onLoad, onError, retryDelay, maxRetries, disableRetry, retries]);

	if (loading && placeholderSrc) {
		return <img src={placeholderSrc} alt={alt} className={`${className} loading`} {...rest} />;
	}

	if (error) {
		return <div className={`${className} error`}>載入圖片失敗</div>;
	}

	return (
		<img
			ref={imgRef}
			src={src ?? ""}
			alt={alt}
			className={className}
			style={{ display: loading ? "none" : src ? "block" : "none" }}
			{...rest}
		/>
	);
};

export default ImageComponent;
